/**
 * Shared horizontal process stepper (field local journeys + office strips).
 * Extracted from FieldShell so lifecycle (A) and local (B) share one chrome.
 */

export type ProcessStepState = "done" | "current" | "todo" | "blocked";

export interface ProcessStep {
  label: string;
  state: ProcessStepState;
  /** Optional click target — when set, step renders as a button. */
  onSelect?: () => void;
}

interface FieldProcessStepperProps {
  steps: ProcessStep[];
  /** `shell` = dark FieldShell bar; `panel` = light workspace / office strip */
  variant?: "shell" | "panel";
  /** Accessible name */
  ariaLabel?: string;
}

export function FieldProcessStepper({
  steps,
  variant = "shell",
  ariaLabel = "Process steps",
}: FieldProcessStepperProps) {
  const rootClass =
    variant === "shell" ? "pg-field-journey" : "pg-field-journey pg-field-journey--panel";

  return (
    <div className={rootClass} role="list" aria-label={ariaLabel}>
      {steps.map((step, index) => {
        const className = `pg-field-journey-step ${step.state}`;
        const body = (
          <>
            <div className="step-number">{index + 1}</div>
            <div className="step-label">{step.label}</div>
          </>
        );
        if (step.onSelect) {
          return (
            <button
              key={`${step.label}-${index}`}
              type="button"
              role="listitem"
              className={`${className} pg-field-journey-step--btn`}
              onClick={step.onSelect}
            >
              {body}
            </button>
          );
        }
        return (
          <div key={`${step.label}-${index}`} role="listitem" className={className}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

/** Build steps from a 0-based active index (classic FieldShell behaviour). */
export function stepsFromActiveIndex(
  labels: string[],
  activeStep: number,
): ProcessStep[] {
  return labels.map((label, index) => ({
    label,
    state:
      index < activeStep ? "done" : index === activeStep ? "current" : "todo",
  }));
}
